import FeatureFlags from './FeatureFlags';
import {
  assignABTest,
  checkTestStatus,
} from '../store/ABTest/ABTest.service';

/**
 * AB Test module to allocate a user to test or control group based on the test percentage
 */
const ABTest = {
  /**
   * Please use the ABTest.model in redux instead of this method. this is maintained for
   * backward compatibility only.
   *
   * Returns a boolean to indicate if the user is in test group based on the test percentage
   * @param {String} testName
   * @param {Number} testPercent - Number between 0 and 1 representing percentage of users
   *                                 who should be opted into the test.
   * @param {Object} param1 Options hash
   * @param {Boolean} param1.skipAssign True to skip assignment and simply return currently assigned
   */
  isInTest: (testName, testPercent, { skipAssign } = {}) => {
    let testStatus = checkTestStatus(testName);

    if (testPercent === 1) {
      return true;
    }

    if (!testStatus.assigned && !skipAssign) {
      testStatus = assignABTest({
        testName,
        variations: {
          control: 1 - testPercent,
          test: testPercent,
        },
      });
    }

    return testStatus.choice;
  },

  /**
   * Gets a value indicating whether or not to enable a test for the current user, taking into
   * consideration the state of the test as a whole.
   * @param {String} testName The name of the test to check
   * @param {Number} testPercent A number between 0 and 1 indicating the probability of being
   * assigned to the test group
   * @param {Object} [opts={}] A hash of options
   * @param {Boolean | Function} [opts.assign=true] True to actually assign the user to the test
   * group, or false to just check the assignment. A function returning the same may also be
   * provided.
   * @yields {Boolean} True if the feature flag associated with `testName` is enabled for the
   * current user AND the user is in the test group.
   */
  isInFeatureTest(testName, testPercent, opts = {}) {
    const options = {
      assign: true,
      ...opts,
    };

    const { assign } = options;

    let assignPromise;

    if (typeof assign === 'function') {
      assignPromise = Promise.resolve(assign());
    } else {
      assignPromise = Promise.resolve(assign);
    }

    return Promise.all([
      FeatureFlags.isEnabled(testName),
      assignPromise,
    ]).then(([enabled, assignValue]) => {
      if (enabled) {
        return this.isInTest(testName, testPercent, { skipAssign: !assignValue });
      }

      return false;
    });
  },
};

export default ABTest;
